import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 1600;
const INTERSECTION_THRESHOLD = 0.3;

/**
 * Counts up from 0 to `target` once the element enters the viewport.
 * Supports formatted numbers with prefix/suffix and "+" decoration.
 *
 * Examples:
 *   <CountUp target={2400} suffix="+" />
 *   <CountUp target={47} />
 *   <CountUp target={9} suffix=" min" />
 *   <CountUp target={0} fallback="0" />
 */
export default function CountUp({
  target,
  suffix = "",
  prefix = "",
  duration = DEFAULT_DURATION_MS,
  formatThousands = true,
  fallback = null,
  className = "",
}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  // If target is non-numeric (e.g. "9 min"), just render fallback.
  const numericTarget =
    typeof target === "number" ? target : Number(target);
  const isNumeric = Number.isFinite(numericTarget);

  useEffect(() => {
    if (!isNumeric) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            setStarted(true);
            const start = performance.now();
            const run = (now) => {
              const elapsed = now - start;
              const p = Math.min(1, elapsed / duration);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(eased * numericTarget));
              if (p < 1) requestAnimationFrame(run);
            };
            requestAnimationFrame(run);
          }
        });
      },
      { threshold: INTERSECTION_THRESHOLD }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [isNumeric, numericTarget, duration, started]);

  if (!isNumeric) {
    return (
      <span ref={ref} className={className}>
        {fallback ?? target}
      </span>
    );
  }

  const display = formatThousands
    ? value.toLocaleString("en-US")
    : String(value);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
